import { partnerPanelHandoff } from '@/lib/api'

/**
 * Opening the partner panel with an active session.
 *
 * ── Why a fetch cannot do this ──
 * Their session cookie is `SameSite=Lax` on `.ninjadex.org`. A browser REFUSES TO STORE
 * a Lax cookie that arrives in a cross-site response, so calling their login with
 * `fetch` from our origin returns `200 success:true` and leaves no session behind —
 * silently, with no error. Measured from our own dev origin:
 *
 *   POST /auth/login  ->  200 success:true
 *   GET  /auth/me     ->  401 "Not logged in"
 *
 * Running the partner's own reference app (`ninjalogin-react`) unmodified reproduces
 * this exactly when it is served from anywhere other than `ninjadex.org`. Their code is
 * fine; the domain it runs on is what decides.
 *
 * ── What does work: a top-level form POST ──
 * For a TOP-LEVEL NAVIGATION the browser treats the destination as the first party, so a
 * `SameSite=Lax` cookie in that response is stored normally. Navigating a tab to their
 * login endpoint therefore plants a real, first-party session.
 *
 * The obstacle was encoding. Their endpoint rejects what forms normally send, and a form
 * cannot send `application/json`:
 *
 *   application/x-www-form-urlencoded   400 INVALID_JSON
 *   multipart/form-data                 422 address is required
 *   ?address= in the query              401 Access token is required
 *   text/plain                          200 + Set-Cookie          <-- and forms can send this
 *
 * `enctype="text/plain"` serialises a field as `name=value`, so the JSON body is split
 * across the name/value boundary and the `=` the browser inserts lands inside a throwaway
 * `pad` field. The result parses as ordinary JSON.
 *
 * This needs nothing from the partner: no `SameSite=None`, no page changes, no CORS
 * entry. It also survives Safari's tracking prevention and Chrome's third-party cookie
 * removal, because at no point is a third-party cookie involved.
 *
 * ── The caller must keep `tab.opener` ──
 * Permission to navigate a cross-origin window comes from being its opener. Clearing it,
 * the usual tabnabbing precaution, makes the final redirect fail with a SecurityError and
 * leaves the tab parked on the login endpoint's raw JSON. See Sidebar.tsx.
 */
/*
 * `||`, NOT `??`, and trimmed.
 *
 * `??` only falls back on null/undefined, and a Docker build declares every VITE_* as an
 * ARG — so one that is not passed arrives as an EMPTY STRING, not as undefined. `??` let
 * that through, PARTNER_API became "", and the login URL resolved RELATIVE to our own
 * origin: the browser posted to defi.ninjadex.org/api/v1/auth/login and nginx answered
 * 405 Not Allowed. Nothing in the build or the container reported a problem.
 *
 * Treating blank as absent is the only version of this that survives a build system that
 * cannot distinguish "unset" from "empty".
 */
const PARTNER_API =
  (import.meta.env.VITE_PARTNER_API_URL as string | undefined)?.trim() ||
  'https://api.ninjadex.org/public'

const LOGIN_URL = `${PARTNER_API}/api/v1/auth/login`

/** Their dashboard. `/client/index.html` and the bare origin both fail — see below. */
const DASHBOARD_FALLBACK = 'https://client.ninjadex.org/index.html'

/**
 * Our own page, shown for the whole hand-off.
 *
 * Opened instead of an empty `window.open('', '_blank')`, which literally IS `about:blank`
 * and leaves that string in the address bar for the entire sign-in. A real path on our
 * origin keeps our domain in the bar from the first frame, and the tab is still ours to
 * drive.
 *
 * It doubles as the page we return to after the POST commits, so the partner's raw JSON
 * response is replaced by something branded rather than by a blank document.
 */
const CONNECTING_PAGE = '/partner-connecting.html'

/**
 * Whether `pathname` is our connecting page, however the host chose to spell it.
 *
 * Production serves the build with `serve -s dist`, which redirects `.html` URLs to their
 * extensionless form: `/partner-connecting.html` 301s to `/partner-connecting`. An
 * equality check against the literal therefore never matches once deployed, the wait for
 * the page times out, and every hand-off silently falls back to the token route — while
 * working perfectly against the dev server, which does no such rewrite.
 */
function isConnectingPage(pathname: string): boolean {
  return pathname === CONNECTING_PAGE || pathname === CONNECTING_PAGE.replace(/\.html$/, '')
}

/**
 * A macrotask that browsers do not clamp.
 *
 * Once the new tab takes focus this one is backgrounded, and a backgrounded tab's
 * `setTimeout` is throttled to roughly one second. The polling below runs entirely in
 * that window, so on `setTimeout` each wait rounded up to ~1s and the partner's raw JSON
 * response stayed on screen for that whole time. A `MessageChannel` message is not
 * subject to the clamp, which brings the same waits down to ~15ms.
 */
function yieldTask(): Promise<void> {
  return new Promise((resolve) => {
    const channel = new MessageChannel()
    channel.port1.onmessage = () => resolve()
    channel.port2.postMessage(0)
  })
}

/**
 * Poll `predicate` until it holds, the tab closes, or `timeoutMs` elapses.
 *
 * The 4ms gate is doing real work. What we are racing is the browser painting the
 * partner's raw JSON response: the document commits, and roughly one display frame later
 * (~16.7ms at 60Hz) it appears on screen. Detecting the commit and issuing the next
 * navigation inside that frame means it never paints. Measured end to end, the window
 * between the JSON committing and our own page being back is ~7.5ms — at 15ms it was
 * ~42ms, which is two to three frames and plainly visible.
 */
async function until(tab: Window, predicate: () => boolean, timeoutMs: number): Promise<boolean> {
  const deadline = performance.now() + timeoutMs
  let lastCheck = 0
  for (;;) {
    if (tab.closed) return false
    const now = performance.now()
    if (now - lastCheck >= 4) {
      lastCheck = now
      if (predicate()) return true
    }
    if (now > deadline) return false
    await yieldTask()
  }
}

/**
 * Whether `tab` is still showing a document of ours.
 *
 * Reading any property of a cross-origin document throws, and that throw is the signal we
 * navigate on: while the tab holds our `about:blank` the read succeeds, and the moment
 * the partner's response commits it does not.
 */
function isOurs(tab: Window): boolean {
  try {
    void tab.document.title
    return true
  } catch {
    return false
  }
}

/**
 * Send `tab` back to our connecting page and wait for it to be ours again.
 *
 * `replace` rather than `href` so the partner's JSON response never enters session
 * history — a Back press from the dashboard should not land on it. The page is already in
 * the tab's cache from the start of the hand-off, so this commits immediately rather than
 * costing another round trip.
 */
async function returnToLoader(tab: Window): Promise<boolean> {
  tab.location.replace(CONNECTING_PAGE)
  return until(tab, () => isOurs(tab) && isConnectingPage(tab.location.pathname), 3000)
}

/**
 * The registrable domain of a hostname, for a SameSite comparison.
 *
 * Naive `endsWith('.ninjadex.org')` would also match `evil-ninjadex.org.attacker.com`; taking
 * the last two labels is what the cookie spec actually compares. Good enough here because
 * both hosts are known to be plain `<sub>.ninjadex.org` — a public-suffix list would be
 * required for the general case (`example.co.uk`).
 */
function registrableDomain(hostname: string): string {
  return hostname.split('.').slice(-2).join('.')
}

/**
 * Whether this page and the partner API are SAME-SITE.
 *
 * ── Why this changes everything ──
 * `SameSite=Lax` blocks a CROSS-SITE request, and "site" means the registrable domain, not
 * the origin. `defi.ninjadex.org` and `api.ninjadex.org` are both `ninjadex.org`, so a
 * request between them is same-site and their Lax session cookie is stored normally.
 *
 * The elaborate tab-driving hand-off below exists because that was NOT true when it was
 * written: the app ran on `localhost` and `aipips.world`, which are genuinely cross-site, and
 * `fetch` there returned `200 success:true` while silently storing nothing. Moving the app to
 * `defi.ninjadex.org` removed the constraint, and their API already returns
 * `access-control-allow-origin: https://defi.ninjadex.org` with `allow-credentials: true`.
 *
 * So on the deployed domain a plain `fetch` is all that is needed — the same call their own
 * login page makes — and it works identically in every browser, including webviews that
 * cannot be handed a driveable tab.
 */
function sameSiteAsPartner(): boolean {
  try {
    return (
      registrableDomain(new URL(PARTNER_API, location.href).hostname) ===
      registrableDomain(location.hostname)
    )
  } catch {
    return false
  }
}

/**
 * Sign in and return where to go, without opening anything.
 *
 * Returns null when the session could not be established, so the caller can fall back rather
 * than navigating to a dashboard that will bounce them straight back to a login form.
 */
export async function signInSameSite(
  address: string,
  /**
   * Called once, on the first NOT_FOUND, to register the wallet upstream before retrying.
   *
   * A genuinely new address has no partner account until our backend delivers one, so the
   * first answer is legitimately NOT_FOUND. Rather than waiting out the retry budget hoping
   * a worker interval lands in time, this asks for the registration directly and then keeps
   * polling -- turning a race against a timer into an ordinary request.
   */
  registerUpstream?: () => Promise<unknown>,
): Promise<string | null> {
  if (!sameSiteAsPartner()) return null

  /*
   * ── Why NOT_FOUND is retried and nothing else is ──
   *
   * A wallet's partner account is created by an OUTBOX: binding a referral enqueues an
   * upstream registration, and the worker dispatches it on a 30-second interval. Between
   * those two moments the partner genuinely has no account for the address and answers
   * NOT_FOUND — so a brand-new user who signs in, binds a code and immediately opens Ninja
   * Army loses a race against a timer.
   *
   * It reads as "new wallets are broken" while old ones work, and it is worse in a webview
   * that goes straight here: the browser flow spends several seconds in its fallback, which
   * often outlasts the window by accident.
   *
   * NOT_FOUND is therefore a "not yet", and waiting is the correct response — the row is
   * already queued and will be delivered. Every other failure is a real one and returns
   * immediately, so a wrong secret or a refused CORS preflight is not hidden behind 20
   * seconds of retries.
   */
  const started = Date.now()
  let askedToRegister = false
  const BUDGET_MS = 20_000
  const GAP_MS = 2_500

  for (;;) {
    let notYet = false

    try {
      const res = await fetch(LOGIN_URL, {
        method: 'POST',
        headers: { 'content-type': 'application/json', accept: 'application/json' },
        // The whole point: same-site, so the Set-Cookie in this response is honoured.
        credentials: 'include',
        /*
         * ── The address ALONE. Do not send referralCode here. ──
         *
         * On this endpoint `referralCode` is not "who referred me" -- it SELECTS which of the
         * address's own partner accounts to sign into. The partner supports several per
         * address and returns them as `availableAccounts` when the pairing misses.
         *
         * We hold the REFERRER's code, which is a different field on their side
         * (`directReferralCode`). Sending it asks for an account that cannot exist, and the
         * answer is NOT_FOUND -- which this function treats as "registration has not landed
         * yet" and retries for twenty seconds before falling back to the partner's login
         * form. Every wallet with a referrer bound therefore failed, always, while the same
         * request without the field succeeds:
         *
         *   {"address":"0xa6e6..."}                           -> success
         *   {"address":"0xa6e6...","referralCode":"NINJADEX"}  -> NOT_FOUND
         *
         * Omitting it lets the partner pick the address's default account, which is what a
         * single-account user has and what this flow has always meant.
         */
        body: JSON.stringify({ address: address.toLowerCase() }),
      })

      const body = (await res.json()) as
        | { success?: boolean; data?: { redirect?: string }; error?: { code?: string } }
        | null

      if (res.ok && body?.success) return body.data?.redirect || DASHBOARD_FALLBACK

      notYet = body?.error?.code === 'NOT_FOUND'
    } catch {
      // Network failure, CORS refusal, or a body that is not JSON. All are real failures.
      return null
    }

    if (!notYet || Date.now() - started + GAP_MS > BUDGET_MS) return null

    if (!askedToRegister) {
      askedToRegister = true
      // Not fatal if it fails: the poll below is still what decides.
      await registerUpstream?.().catch(() => undefined)
    }

    await new Promise((r) => setTimeout(r, GAP_MS))
  }
}

export interface PanelDestination {
  url: string
  /** Which route produced it — worth logging when a hand-off misbehaves. */
  via: 'direct' | 'token'
}

/**
 * Sign the wallet into the partner and take `tab` all the way to their dashboard.
 *
 * ── The sequence, and why each step is there ──
 *   1. Paint our loader, so the tab never sits on a blank page.
 *   2. Submit the top-level POST immediately. This is what plants the session; it is not
 *      waited on behind anything else, because the round trip is the slowest part.
 *   3. In parallel, ask for the authoritative `redirect`. Their answer is currently always
 *      the dashboard, so a failure here costs nothing — we already have the same value.
 *   4. The instant the POST commits, send the tab back to a blank document of ours and
 *      repaint the loader. Without this the browser keeps showing the partner's raw JSON
 *      response for the entire time the dashboard is loading, which is what the user sees
 *      as "the API response flashed up".
 *   5. Navigate to the dashboard. The loader stays on screen until it commits.
 *
 * `referralCode` is deliberately NOT sent. It disambiguates between several partner
 * accounts on ONE address -- but it names one of THAT ADDRESS'S OWN codes, and the code we
 * hold is the referrer's, a different field on their side (`directReferralCode`). Passing it
 * asks for a pairing that cannot exist and is answered NOT_FOUND every time. Omitting it
 * lets the partner choose the address's default account, which is what a single-account
 * user has. A genuinely multi-account address would answer `need_account_select`, which we
 * have no UI for either way -- an unhandled prompt beats a guaranteed failure.
 */
export async function openPartnerPanel(
  tab: Window | null,
  address: string,
): Promise<PanelDestination> {
  if (!tab) {
    // Popup blocked. Nothing here can create a session without a tab to navigate, so the
    // server-minted hand-off is all that is left.
    const { url } = await partnerPanelHandoff()
    return { url, via: 'token' }
  }

  /*
   * Wait for CONNECTING_PAGE to actually COMMIT, not merely for a document we can touch.
   *
   * A freshly opened tab starts on an initial `about:blank` that is also same-origin and
   * also has a body, so a laxer check passes within a few milliseconds — before our page
   * has loaded. Submitting the form at that point cancels the pending navigation, and the
   * tab then sits on a blank white document for the whole sign-in instead of showing the
   * loader. Matching on the pathname is what distinguishes the two.
   */
  const ready = until(
    tab,
    // Both conditions matter. `pathname` flips as soon as the navigation COMMITS, which
    // is before the parser has produced a `<body>` — appending the form at that instant
    // throws on a null body and the sign-in never starts.
    () => isOurs(tab) && isConnectingPage(tab.location.pathname) && Boolean(tab.document.body),
    5000,
  )

  // Started now, read later. Blocking the POST on this added ~2s during which nothing was
  // happening in the tab.
  const redirectPromise = fetch(LOGIN_URL, {
    method: 'POST',
    headers: { 'content-type': 'application/json', accept: 'application/json' },
    credentials: 'include',
    // Address alone -- see signInSameSite above for why referralCode must not be sent.
    body: JSON.stringify({ address: address.toLowerCase() }),
  })
    .then((res) => res.json())
    .then((body: { success?: boolean; data?: { redirect?: string } } | null) =>
      body?.success && body.data?.redirect ? body.data.redirect : DASHBOARD_FALLBACK,
    )
    .catch(() => DASHBOARD_FALLBACK)

  try {
    /*
     * Built through the DOM rather than written as HTML. The field NAME carries JSON
     * containing quotes and braces, and hand-escaping that into markup is exactly the kind
     * of thing that works until an address or code contains something unexpected.
     */
    if (!(await ready)) throw new Error('connecting page did not load')

    const doc = tab.document
    const form = doc.createElement('form')
    form.method = 'POST'
    form.action = LOGIN_URL
    form.enctype = 'text/plain'

    const input = doc.createElement('input')
    input.type = 'hidden'
    // `pad` absorbs the "=" that text/plain encoding inserts between name and value.
    // Address alone. `referralCode` selects one of the address's OWN partner accounts, and
    // the code we hold is the referrer's -- sending it guarantees NOT_FOUND.
    const fields = [`"address":"${address.toLowerCase()}"`]
    input.name = `{${fields.join(',')},"pad":"`
    input.value = `"}`

    form.appendChild(input)
    doc.body.appendChild(form)
    form.submit()

    // Eight seconds is generous for one POST and short enough that a wedged tab does not
    // strand the user on a spinner.
    const planted = await until(tab, () => !isOurs(tab), 8000)

    if (planted) {
      // Reclaim the tab before the dashboard starts loading, so the partner's JSON is
      // on screen for a frame rather than for the whole navigation.
      await returnToLoader(tab)

      const url = await redirectPromise
      tab.location.href = url
      return { url, via: 'direct' }
    }
  } catch {
    // A closed tab, or a document we lost access to mid-flight. Both fall through.
  }

    const { url } = await partnerPanelHandoff()
  return { url, via: 'token' }
}
